import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { RequestedSupportingItem } from '@/features/jobs/details/tax-type/info-gathering/RequestedSupportingItem'
import type { BusinessInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/business-info/BusinessInfoSupportingItemInput'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { InfoGatheringStore } from '@/features/jobs/details/tax-type/info-gathering/InfoGatheringStore'
import type {
  FetchBusinessProfileForJobFn,
  BusinessProfileEditorViewModelFactory,
  ListBusinessProfilesFn,
  PromptDeleteBusinessProfileFn,
} from '@/features/tax-profile'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { BusinessProfileVersion } from '@/features/tax-profile/business-profiles/BusinessProfile'
import { ignoreAbortError } from '@/utils/abort-util'
import { debounceTime, filter, map, merge, Subject, switchMap } from 'rxjs'
import { filterNonNil, withAbortSignal } from '@/utils/rx-util'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'

/**
 * View model for a business info supporting item.
 */
export class BusinessInfoRequestedSupportingItemViewModel
  implements ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * @see {next$}
   * @private
   */
  private readonly _nextSubject = new Subject<unknown>()

  /**
   * Emits when we intend to navigate to the next item.
   */
  readonly next$ = this._nextSubject.asObservable()

  /**
   * Editor VM for the business profile.
   */
  readonly businessProfileEditorVm

  /**
   * The supporting item input type.
   */
  readonly type = 'BusinessInfo' as const

  /**
   * The business profile version associated with this item, if any.
   */
  @observable accessor profile: BusinessProfileVersion | null = null

  /**
   * Tracks whether we're currently creating our first profile.
   * Used to conditionally emit on {@link next$}.
   *
   * @private
   */
  private accessor _isCreatingFirstProfile = false

  /**
   * Dialog for showing the business profiles list.
   */
  readonly businessProfilesDialog = new ModalViewModel()

  /**
   * Infinite scroll provider for existing business profiles in the workspace.
   */
  readonly existingBusinessProfilesScroller: InfiniteScrollProvider<BusinessProfileVersion>

  constructor(
    private readonly _job: SubmittedJob,
    public readonly item: RequestedSupportingItem<BusinessInfoSupportingItemInput>,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    createBusinessProfileEditorViewModel: BusinessProfileEditorViewModelFactory,
    private _fetchBusinessProfileForJob: FetchBusinessProfileForJobFn,
    private readonly _listBusinessProfiles: ListBusinessProfilesFn,
    private readonly _promptDeleteBusinessProfile: PromptDeleteBusinessProfileFn,
  ) {
    this.businessProfileEditorVm = createBusinessProfileEditorViewModel({
      onSaved: (profile) => this.provideProfile(profile),
    })

    this.existingBusinessProfilesScroller = new InfiniteScrollProvider({
      fetchItems: ({ paging, signal }) =>
        this._listBusinessProfiles(
          {
            paging,
          },
          signal,
        ),
      limit: 1000,
    })

    // Reload the profile when the provided profile changes.
    this._lifecycle.manage(
      this.item.input.providedChanges$.pipe(
        debounceTime(200),
        filter(
          (provided) =>
            !this.profile?.equalsIdentity(
              provided?.profileId,
              provided?.profileVersion,
            ),
        ),
        switchMap(() => withAbortSignal((signal) => this._fetchCore(signal))),
      ),
    )

    // Remove profile from list when delisted
    const delisted$ =
      this.businessProfileEditorVm.businessProfileDelisted$.pipe(
        map((id) =>
          this.existingBusinessProfilesScroller.items.find((p) => p.id === id),
        ),
        filterNonNil(),
      )

    // Remove older profile versions when a new version is added
    const profileVersionSuperseded$ =
      this.businessProfileEditorVm.businessProfileAdded$.pipe(
        map((newVersion) =>
          this.existingBusinessProfilesScroller.items.find(
            (loaded) =>
              loaded.id === newVersion.id &&
              loaded.version !== newVersion.version,
          ),
        ),
        filterNonNil(),
      )

    const profileRemoved$ = merge(profileVersionSuperseded$, delisted$)

    this._lifecycle.manage(
      syncInfiniteScrollerItems({
        scroller: this.existingBusinessProfilesScroller,
        additions$: this.businessProfileEditorVm.businessProfileAdded$,
        removals$: profileRemoved$,
      }),
    )
  }

  /**
   * Whether the item is considered completed.
   */
  @computed get isCompleted() {
    return (
      this.item.isCompleted &&
      // Only treat as completed once we have the profile's business name.
      Boolean(this.businessName)
    )
  }

  /**
   * Gets the business name.
   */
  @computed get businessName() {
    if (!this.profile) {
      return ''
    }

    return this.profile.businessInfo.businessName
  }

  /**
   * Whether the item is read-only.
   */
  @computed get isReadOnly() {
    return this._job.status === 'Closed'
  }

  /**
   * Whether we're currently fetching the data needed to display the editor.
   */
  @computed get isFetchingEditorData() {
    return (
      this.fetch.pending || this.existingBusinessProfilesScroller.fetch.pending
    )
  }

  /**
   * Other available business profiles, excluding the current one, based on
   * the version.
   */
  @computed get otherAvailableProfiles() {
    if (!this.profile) {
      return this.existingBusinessProfilesScroller.items
    }

    return this.existingBusinessProfilesScroller.items.filter(
      (p) => !p.equalsIdentity(this.profile?.id, this.profile?.version),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.prepare(this._lifecycle.signal).catch(ignoreAbortError)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.businessProfilesDialog.close()
    this.businessProfileEditorVm.close()
  }

  /**
   * Sets the profile.
   */
  @action.bound
  setProfile(profile: BusinessProfileVersion | null) {
    this.profile = profile
    this.businessProfileEditorVm.reset(profile)
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  showDialog() {
    this.businessProfileEditorVm.showDialog(this.profile)
  }

  /**
   * Shows the business profiles selection dialog, or the editor if relevant.
   */
  @action.bound
  showBusinessProfilesDialog() {
    if (this.isReadOnly) {
      // When read-only with a profile, show the editor dialog.
      // This lets the user see which profile they chose in a read-only view.
      if (this.profile) {
        this.showEditorDialog(this.profile)
      }

      return
    }

    // If no existing profiles, show the editor directly
    if (this.existingBusinessProfilesScroller.items.length === 0) {
      this.showEditorDialog(null)
    } else {
      this.businessProfilesDialog.show()
    }
  }

  /**
   * Shows the editor dialog for creating/editing a profile.
   */
  @action.bound
  showEditorDialog(profile: BusinessProfileVersion | null = null) {
    this.businessProfileEditorVm.showDialog(profile)
  }

  /**
   * Prepares the view model by fetching all relevant data.
   *
   * @param signal
   */
  async prepare(signal?: AbortSignal) {
    await Promise.all([
      this.fetch(signal),
      this.existingBusinessProfilesScroller.fetch({ signal: signal }),
    ])

    signal?.throwIfAborted()

    // Track whether this is the first profile we're creating.
    this._isCreatingFirstProfile =
      this.profile === null && this.otherAvailableProfiles.length === 0
  }

  /**
   * Provides a business profile to the job.
   */
  provideProfile = task.resolved(async (profile: BusinessProfileVersion) => {
    await this._infoGatheringStore.provideBusinessProfile(
      this._job.id,
      this.item,
      profile,
    )

    this.setProfile(profile)

    if (this._isCreatingFirstProfile) {
      this._nextSubject.next(undefined)
      this._isCreatingFirstProfile = false
    }
  })

  /**
   * Prompts for and performs permanent deletion of a business profile.
   */
  promptDeleteProfilePermanently = task.resolved(
    async (profile: BusinessProfileVersion) => {
      // Prevent deletion of the currently selected profile
      if (this.profile?.id === profile.id) {
        return
      }

      const wasDeleted = await this._promptDeleteBusinessProfile(
        profile,
        this._lifecycle.signal,
      )

      if (wasDeleted) {
        // If the deleted profile was the current one, clear it (double-check)
        if (this.profile?.id === profile.id) {
          this.setProfile(null)
        }
      }
    },
  )

  /**
   * Fetches the profile if one has been provided.
   */
  fetch = task.resolved(async (signal?: AbortSignal) => {
    await this._fetchCore(signal)
  })

  /**
   * Core fetch logic that doesn't reset task state.
   * @private
   */
  private async _fetchCore(signal?: AbortSignal) {
    if (!this.item.input.provided) {
      // Underlying item has no provided value, no need to fetch.
      this.setProfile(null)
      return
    }

    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to retrieve business profile information',
      signal,
    )
    const profile = await withRetry(() =>
      this._fetchBusinessProfileForJob(this._job.id, signal),
    )

    signal?.throwIfAborted()
    this.setProfile(profile)
  }
}
